import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';
import {
  HUB_WORLDS,
  HIDDEN_WORLDS_KEY,
  migrateHiddenWorlds,
  destinationAllowedForRole,
  destinationAllowedByToggle,
  hubCardWorlds,
  homeOptionCount,
} from './hubWorlds';
import { FROM_WORLD_QUERY } from './hubWorldLookup';
import {
  AI_CENTER_MODULES,
  isAiCenterModuleEnabled,
} from '../aiCenter/aiCenterModules';

export function useHubWorlds() {
  const router = useRouter();
  const store = useStore();
  const { accountScopedRoute } = useAccount();
  const { isStacklab } = useConfig();
  const { shouldShow, isFeatureFlagEnabled } = usePolicy();
  const { isAdmin } = useAdmin();
  const { uiSettings, updateUISettings } = useUISettings();
  const { enabled: onboardingSurface } = useOnboardingSurface();
  const dashboardApps = useMapGetter('dashboardApps/getNavigationApps');

  onMounted(() => {
    store.dispatch('dashboardApps/get');
  });

  const findRouteByName = name => {
    if (!name) return null;
    return router.getRoutes().find(route => route.name === name) || null;
  };

  const isDestinationAllowed = destination => {
    if (destination.aiCenterModule) {
      const modules =
        destination.aiCenterModule === 'any'
          ? AI_CENTER_MODULES.map(item => item.id)
          : [destination.aiCenterModule];
      if (
        !modules.some(id => isAiCenterModuleEnabled(id, isFeatureFlagEnabled))
      )
        return false;
    }
    if (!destinationAllowedForRole(destination, isAdmin.value)) return false;
    // store.getters é lido dentro do computed, então o destino reaparece
    // sozinho quando kanban/fetchKanbanConfig responde no boot.
    if (!destinationAllowedByToggle(destination, store.getters)) return false;
    if (destination.stacklabOnly && !isStacklab) return false;
    if (destination.desktopOnly && !onboardingSurface.value) return false;
    if (
      destination.featureFlag &&
      !isFeatureFlagEnabled(destination.featureFlag)
    ) {
      return false;
    }

    const targetName =
      destination.params?.navigationPath || destination.routeName;
    const meta = findRouteByName(targetName)?.meta || {};
    return shouldShow(
      destination.featureFlag || meta.featureFlag || '',
      meta.permissions ?? [],
      meta.installationTypes ?? []
    );
  };

  const toFor = (destination, worldId) =>
    accountScopedRoute(destination.routeName, destination.params || {}, {
      ...(destination.query || {}),
      [FROM_WORLD_QUERY]: worldId,
    });

  const hiddenWorldIds = computed(() => migrateHiddenWorlds(uiSettings.value));

  const appDestinations = computed(() =>
    (dashboardApps.value || []).map(app => ({
      id: `app_${app.id}`,
      label: app.title,
      subtitleKey: 'HUB.SUBTITLES.DASHBOARD_APP',
      icon: resolveSavedIconClass(
        typeof app.icon === 'string' && /^i-(ri|lucide)-/.test(app.icon)
          ? app.icon
          : 'i-ri-link'
      ),
      routeName: 'dashboard_apps_show',
      params: { id: app.id },
      to: accountScopedRoute(
        'dashboard_apps_show',
        { id: app.id },
        { [FROM_WORLD_QUERY]: 'system' }
      ),
    }))
  );

  const worldsWithDestinations = computed(() =>
    HUB_WORLDS.map(world => {
      const destinations = world.destinations
        .filter(isDestinationAllowed)
        .map(destination => ({
          ...destination,
          to: toFor(destination, world.id),
        }));

      if (world.id === 'system') {
        destinations.push(...appDestinations.value);
      }

      const primaryDestination = destinations.find(
        destination => destination.id === world.primaryAction?.destinationId
      );

      return {
        ...world,
        destinations,
        destinationCount: destinations.length,
        // O card do Hub conta o que a tela de início mostra, não o mapa.
        optionCount: homeOptionCount({ ...world, destinations }),
        hidden: hiddenWorldIds.value.includes(world.id),
        primaryDestination,
      };
    }).filter(world => world.destinationCount > 0)
  );

  // Tudo o que existe no mapa (inclusive Medir e Configurar, sem card):
  // a tela de início de uma área e a barra "Voltar" olham aqui.
  const allWorlds = worldsWithDestinations;
  const preferenceWorlds = computed(() =>
    hubCardWorlds(worldsWithDestinations.value)
  );
  const visibleWorlds = computed(() =>
    preferenceWorlds.value.filter(world => !world.hidden)
  );

  const setWorldVisible = (worldId, visible) => {
    const next = new Set(hiddenWorldIds.value);
    if (visible) next.delete(worldId);
    else next.add(worldId);
    updateUISettings({ [HIDDEN_WORLDS_KEY]: Array.from(next) });
  };

  const showAllWorlds = () => {
    if (!hiddenWorldIds.value.length) return;
    updateUISettings({ [HIDDEN_WORLDS_KEY]: [] });
  };

  return {
    allWorlds,
    preferenceWorlds,
    visibleWorlds,
    hiddenWorldIds,
    setWorldVisible,
    showAllWorlds,
  };
}
